import { cn } from '@silospot/ui/lib/utils';
import * as Flags from 'country-flag-icons/react/3x2';
import type { ReactElement } from 'react';

type FlagComp = (props: { title?: string; className?: string }) => ReactElement;

/**
 * Real SVG country flag (self-hosted, inlined). Renders identically on every OS
 * — including Windows/Chrome, which do not draw regional-indicator flag emoji.
 * Returns null for an unknown or blank code so callers can fall back to '—'.
 */
export function Flag({
  country,
  className,
}: {
  country: string;
  className?: string;
}): ReactElement | null {
  const cc = (country || '').toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return null;
  const Comp = (Flags as Record<string, FlagComp | undefined>)[cc];
  if (!Comp) return null;
  return (
    <span
      className={cn(
        'inline-block h-[13px] w-[19px] shrink-0 overflow-hidden rounded-[2px] align-[-2px] ring-1 ring-black/5',
        className,
      )}
    >
      <Comp title={cc} className="h-full w-full object-cover" />
    </span>
  );
}

/** ISO-3166-1 alpha-2 → localised country name. Falls back to the code itself. */
export function countryName(country: string, locale?: string): string {
  const cc = country.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return country || '';
  try {
    return new Intl.DisplayNames([locale ?? 'en'], { type: 'region' }).of(cc) ?? cc;
  } catch {
    return cc;
  }
}

function label(city: string, country: string): string {
  if (city && country) return `${city}, ${country}`;
  return city || country || '';
}

/** Flag + "City, CC" (geo facts only — no provider name). '—' when unknown. */
export function Location({ city, country }: { city: string; country: string }): ReactElement {
  const text = label(city, country);
  if (!text) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <Flag country={country} />
      {text}
    </span>
  );
}
